import { SignerFieldsWarning } from "./SignerFieldsWarning";
import { SIGNER_COLORS } from "../SignatureRequestPdfLayoutEditor.constants";
import { Button, Typography } from "@/components/ui";
import { Avatar, AvatarFallback } from "@/components/ui/shadcn/Avatar/Avatar";
import type { DirectoryUser } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatUsersName } from "@/lib/utils/format";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";

type UserSignerRowProps = {
  user: DirectoryUser;
  index: number;
  colorIndex?: number;
  hasFields: boolean;
  slotIndex: number;
  isOrdered: boolean;
  signerId: string;
  currentSignerCount: number;
  isActive: boolean;
  onSelect: () => void;
  onRemove: () => void;
};

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function UserSignerRow({
  user,
  index,
  colorIndex,
  hasFields,
  slotIndex,
  isOrdered,
  signerId,
  currentSignerCount,
  isActive,
  onSelect,
  onRemove,
}: UserSignerRowProps) {
  const userName = formatUsersName(user) || `Firm signer ${slotIndex + 1}`;
  const color = SIGNER_COLORS[(colorIndex ?? index) % SIGNER_COLORS.length];
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: signerId, disabled: !isOrdered });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "hover:bg-muted/40 flex min-h-16 cursor-pointer items-center gap-3 px-4 py-3 transition-colors",
        isActive && "bg-primary/5 ring-primary ring-1 ring-inset",
        isDragging && "relative z-10 shadow-sm",
        index < currentSignerCount - 1 && "border-b",
      )}
    >
      {isOrdered ? (
        <button
          type="button"
          className="text-muted-foreground flex h-8 w-6 cursor-grab items-center justify-center active:cursor-grabbing"
          aria-label="Reorder signer"
          onClick={(event) => {
            event.stopPropagation();
          }}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      ) : null}
      <Avatar className="h-9 w-9">
        <AvatarFallback className={cn("text-xs font-semibold", color.pill)}>
          {getInitials(userName)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Typography
            className={cn("truncate text-sm font-semibold", color.text)}
          >
            {userName}
          </Typography>
          {!hasFields ? <SignerFieldsWarning kind="user" /> : null}
        </div>
        <Typography className="text-muted-foreground text-xs">
          Firm signer {slotIndex + 1} · Firm
        </Typography>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-muted-foreground h-8 w-8 shrink-0"
        aria-label={`Remove ${userName}`}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
